<template>
  <div class="route-plan">
    <div class="search-form">
      <Form :label-width="80" :model="formData" inline>
        <FormItem label="起点">
          <div class="point-row">
            <Input v-model="formData.start" style="width: 260px" placeholder="输入地址，或点右侧按钮在地图上选" />
            <Button :type="picking === 'start' ? 'primary' : 'default'" @click="togglePick('start')">
              {{ picking === 'start' ? '取消选点' : '地图选点' }}
            </Button>
          </div>
        </FormItem>
        <FormItem label="终点">
          <div class="point-row">
            <Input v-model="formData.end" style="width: 260px" placeholder="输入地址，或点右侧按钮在地图上选" />
            <Button :type="picking === 'end' ? 'primary' : 'default'" @click="togglePick('end')">
              {{ picking === 'end' ? '取消选点' : '地图选点' }}
            </Button>
          </div>
        </FormItem>
        <FormItem label="途经点">
          <div class="point-rows">
            <div class="point-row" v-for="(w, i) in formData.waypoints" :key="i">
              <Input v-model="formData.waypoints[i]" style="width: 260px" placeholder="选填，可多个" />
              <Button size="small" :type="picking === 'wp' + i ? 'primary' : 'default'" @click="togglePick('wp' + i)">
                {{ picking === 'wp' + i ? '取消' : '地图选点' }}
              </Button>
              <Button size="small" type="error" ghost @click="removeWaypoint(i)">删除</Button>
            </div>
            <div class="point-row">
              <Button size="small" @click="addWaypoint">+ 添加途经点</Button>
            </div>
          </div>
        </FormItem>
        <FormItem label="匹配距离">
          <InputNumber v-model="routeThreshold" :min="500" :max="20000" :step="500" style="width: 120px"></InputNumber>
          <span style="margin-left: 4px">米</span>
        </FormItem>
        <!--仅管理员/超管/运营/财务与合伙人可选车队：管理员是从全部车队里筛，合伙人只能筛自己名下-->
        <FormItem label="车队" v-if="canFilterCompany">
          <Select v-model="formData.company_id" placeholder="全部站点" filterable clearable style="width: 220px" @on-change="onCompanyChange">
            <Option v-for="item in companyList" :key="item.company_id" :value="item.company_id">{{ item.company_name }}</Option>
          </Select>
        </FormItem>
        <!--是否把高德加油站POI算进沿线结果：受限角色根本不给这个开关，他们只看名下上站的站点-->
        <FormItem label="沿线图层" v-if="!isRestrictedRole">
          <Checkbox v-model="showAmapPoi" @on-change="onToggleAmap">显示高德加油站</Checkbox>
        </FormItem>
        <FormItem label=" ">
          <Button type="primary" :loading="routeLoading" @click="planRoute">规划路线</Button>
          <Button @click="clearAll" style="margin-left: 8px">清空</Button>
        </FormItem>
      </Form>
      <div class="route-tabs" v-if="routeList.length">
        <span class="tabs-label">推荐方案：</span>
        <Button v-for="(r, i) in routeList" :key="i" size="small"
                :type="activeRouteIndex === i ? 'primary' : 'default'"
                style="margin-right: 6px" @click="selectRoute(i)">
          方案{{ i + 1 }}：{{ (r.distance / 1000).toFixed(1) }}km / {{ Math.round(r.duration / 60) }}分钟
        </Button>
        <span class="route-tip">
          <i class="dot" style="background:#19be6b"></i>我们的站点
          <template v-if="canShowAmap">
            <i class="dot" style="background:#ffb300"></i>高德加油站
          </template>
          <span style="margin-left:8px">共匹配沿线站点 {{ matchedCount }} 个（我们的 {{ ourMatchedCount }} 个<template v-if="canShowAmap">，高德加油站 {{ poiMatchedCount }} 个</template>）：点聚合数字直接聚焦展开该区域</span>
        </span>
      </div>
      <!-- 地图选点提示条 -->
      <div class="pick-tip" v-if="picking">
        正在为「{{ pickLabel }}」选点：请在地图上单击目标位置，选点后会自动回填地址；再次点击「取消选点」可退出
      </div>
    </div>
    <div class="map-box" :style="{ height: mapHeight + 'px' }" id="routeMap"></div>
  </div>
</template>

<script>
  //高德 JSAPI 2.0 安全密钥：Geocoder/PlaceSearch 等服务接口必需（与站点地图页保持一致）
  window._AMapSecurityConfig = {
    securityJsCode: "20341665b1257613258851e2f4d03f9b"
  };
  import AMapLoader from '@amap/amap-jsapi-loader';
  export default {
    name: "mapRoutePlan",
    data() {
      return {
        formData: {
          start: '',
          end: '',
          waypoints: [],
          //车队筛选：选了之后“我们的站点”只在该车队的可加油站点里匹配
          company_id: '',
        },
        companyList: [],
        mapHeight: 500,
        //地图选点：''=未选点，'start'/'end'/'wp0'...=正在为哪个点选点
        picking: '',
        routeThreshold: 5000,
        routeLoading: false,
        routeList: [],
        activeRouteIndex: 0,
        matchedCount: 0,
        //沿线匹配结果的来源统计：我们的站点 / 高德加油站POI
        ourMatchedCount: 0,
        poiMatchedCount: 0,
        //受限账号（合伙人/车队）后端只返回名下站点，同时不再检索高德加油站POI
        restrictPoi: false,
        //是否把高德加油站POI作为沿线候选展示（仅非受限账号可调）
        showAmapPoi: true,
      }
    },
    computed: {
      //高德加油站图层是否参与匹配与展示：受限账号强制不展示，其余角色看勾选
      canShowAmap(){
        return !this.restrictPoi && this.showAmapPoi;
      },
      //受限角色（3=合伙人、4=车队/车队长）：只看自己名下上站的站点，后端不返回车辆也不让检索高德POI
      //按角色判断而不等接口回包，避免刚进页面时短暂露出不属于他的控件
      isRestrictedRole(){
        return [3, 4].indexOf(parseInt(sessionStorage.getItem('groupID') || '0')) >= 0;
      },
      //可选车队的角色：1/2=管理员与超管、7=运营、8=财务看全部车队；3=合伙人（后端只回名下车队）
      canFilterCompany(){
        return [1, 2, 3, 7, 8].indexOf(parseInt(sessionStorage.getItem('groupID') || '0')) >= 0;
      },
      pickLabel(){
        if (this.picking === 'start') return '起点';
        if (this.picking === 'end') return '终点';
        if (this.picking && this.picking.indexOf('wp') === 0) return '途经点' + (Number(this.picking.slice(2)) + 1);
        return '';
      }
    },
    methods: {
      // 根据屏幕高度动态计算地图容器高度：视口高度 - 容器顶部位置 - 底部留白
      calcMapHeight(){
        const el = document.querySelector('.map-box');
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const height = window.innerHeight - top - 20;
        this.mapHeight = height > 300 ? height : 300;
        this.$nextTick(() => {
          if (this.map && typeof this.map.resize === 'function') this.map.resize();
        });
      },
      async initMap(){
        AMapLoader.load({
          key: "c3219f1ed0f4493a4f3ca6ead5e51d37",     // 申请好的Web端开发者Key，首次调用 load 时必填
          version: "2.0",      // 指定要加载的 JSAPI 的版本，缺省时默认为 1.4.15
          plugins: ['AMap.MarkerCluster', 'AMap.Geocoder', 'AMap.PlaceSearch'],       // 需要使用的的插件列表
        }).then((AMap)=>{
          this.AMap = AMap;
          this.map = new AMap.Map("routeMap", { zoom: 5, center: [108.55, 34.32] });
          //地理编码/逆地理编码
          this.geocoder = new AMap.Geocoder({ city: '全国' });
          //地图点击：处于选点状态时取点击坐标
          this.map.on('click', (e)=>{ this.onMapClick(e); });
        }).catch(e=>{
          console.log(e);
        })
      },
      //———— 地图选点 ————
      //点按钮进入/退出选点模式
      togglePick(key){
        this.picking = this.picking === key ? '' : key;
      },
      //地图点击取点：记录坐标 + 逆地理回填地址文本
      async onMapClick(e){
        if (!this.picking) return;
        let lng = e.lnglat.getLng(), lat = e.lnglat.getLat();
        let coord = [lng, lat];
        let addr = await this.reverseGeocode(coord);
        let key = this.picking;
        if (key === 'start') this.formData.start = addr || (lng.toFixed(6) + ',' + lat.toFixed(6));
        else if (key === 'end') this.formData.end = addr || (lng.toFixed(6) + ',' + lat.toFixed(6));
        else this.$set(this.formData.waypoints, Number(key.slice(2)), addr || (lng.toFixed(6) + ',' + lat.toFixed(6)));
        //文本可能仍是模糊地址，规划时优先用这里拿到的精确坐标
        this.pickedCoords[key] = coord;
        this.drawPickedPoints();
        this.picking = '';
      },
      //逆地理编码：坐标→地址文本
      reverseGeocode(coord){
        return new Promise((resolve)=>{
          this.geocoder.getAddress(coord, (status, result)=>{
            if (status === 'complete' && result.regeocode) resolve(result.regeocode.formattedAddress);
            else resolve('');
          });
        });
      },
      //把已选取的起终点/途经点画到地图上（起点绿、终点红、途经点蓝）
      drawPickedPoints(){
        if (this.endpointMarkers){ this.map.remove(this.endpointMarkers); this.endpointMarkers = null; }
        let mk = [];
        Object.keys(this.pickedCoords).forEach((key)=>{
          let color = key === 'start' ? '#19be6b' : (key === 'end' ? '#f56c6c' : '#2d8cf0');
          mk.push(new this.AMap.Marker({
            position: this.pickedCoords[key],
            zIndex: 210,
            bubble: true,
            offset: new this.AMap.Pixel(-7, -7),
            content: '<div style="width:14px;height:14px;border-radius:50%;background:' + color + ';border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
          }));
        });
        this.map.add(mk);
        this.endpointMarkers = mk;
      },
      addWaypoint(){ this.formData.waypoints.push(''); },
      removeWaypoint(i){
        this.formData.waypoints.splice(i, 1);
        this.drawPickedPoints();
      },
      //文本→坐标：支持直接填 "经度,纬度"；其余按结构化地址优先 Geocoder、名称优先 PlaceSearch，互为兜底
      async resolveAddressSmart(text){
        if (!text) return null;
        let m = String(text).trim().match(/^(\d{2,3}\.\d{3,})\s*,\s*(\d{1,3}\.\d{3,})$/);
        if (m) return [parseFloat(m[1]), parseFloat(m[2])];
        let isStructured = /[省市区县路街道村镇号]/.test(text) && text.length >= 8;
        let c = null;
        if (isStructured){ c = await this.geocodeAddress(text); if (!c) c = await this.locateByPoi(text); }
        else { c = await this.locateByPoi(text); if (!c) c = await this.geocodeAddress(text); }
        return c;
      },
      //取点坐标：地图选过的直接用选点结果，否则解析文本
      async resolvePoint(key, text){
        if (this.pickedCoords[key]) return this.pickedCoords[key];
        return await this.resolveAddressSmart(text);
      },
      //规划主流程：解起点/终点/途经点 → 后端代理调高德REST算路 → 画线 + 沿线匹配
      async planRoute(){
        if (!this.formData.start || !this.formData.end){
          this.$Message.warning('请先填写起点和终点');
          return;
        }
        this.routeLoading = true;
        try{
          let start = await this.resolvePoint('start', this.formData.start);
          let end = await this.resolvePoint('end', this.formData.end);
          if (!start || !end){ this.$Message.warning('起点或终点无法定位，请检查地址'); this.routeLoading = false; return; }
          let wpArr = [];
          for (let i = 0; i < this.formData.waypoints.length; i++){
            let text = this.formData.waypoints[i];
            if (!text) continue;
            let c = await this.resolvePoint('wp' + i, text);
            if (c) wpArr.push(c[0] + ',' + c[1]);
          }
          let res = await this.$commonFun.postProxy('admin/gas/routeDriving', {
            origin: start[0] + ',' + start[1],
            destination: end[0] + ',' + end[1],
            waypoints: wpArr.join('|'),
          });
          let routes = this.buildRoutes(res && res.data ? res.data : null);
          if (!routes.length){ this.$Message.info('未找到可行路线'); this.routeLoading = false; return; }
          this.routeList = routes;
          this.activeRouteIndex = 0;
          this.drawActiveRoute();
          await this.matchRouteStations();
          this.$Message.success('路线规划完成');
        }catch(e){
          console.error('路线规划失败:', e);
          this.$Message.error('路线规划失败');
        }
        this.routeLoading = false;
      },
      //解析高德 driving 返回：取 paths → 解码 steps.polyline 为 [[lng,lat],...]，按里程排序
      buildRoutes(amap){
        let routes = [];
        if (amap && amap.status === '1' && amap.route && amap.route.paths){
          amap.route.paths.forEach((path)=>{
            let pts = this.decodeSteps(path.steps);
            if (pts.length > 1){
              routes.push({ points: pts, distance: Number(path.distance || 0), duration: Number(path.duration || 0) });
            }
          });
        }
        routes.sort((a, b)=> a.distance - b.distance);
        return routes;
      },
      decodeSteps(steps){
        let pts = [];
        if (!Array.isArray(steps)) return pts;
        steps.forEach((st)=>{
          if (st.polyline){
            st.polyline.split(';').forEach((c)=>{
              let [lng, lat] = c.split(',').map(Number);
              if (!isNaN(lng) && !isNaN(lat)) pts.push([lng, lat]);
            });
          }
        });
        return pts;
      },
      //绘制当前方案折线，并自适应视野
      drawActiveRoute(){
        let route = this.routeList[this.activeRouteIndex];
        if (!route) return;
        if (this.routePolyline){ this.map.remove(this.routePolyline); this.routePolyline = null; }
        this.routePolyline = new this.AMap.Polyline({
          map: this.map,
          path: route.points,
          strokeColor: '#1aad19',
          strokeWeight: 6,
          strokeOpacity: 0.9,
          lineJoin: 'round',
        });
        this.drawPickedPoints();
        this.map.setFitView([this.routePolyline], false, [40, 40, 40, 40], 12);
      },
      //切换方案：重画折线 + 重新沿线匹配
      async selectRoute(i){
        if (i === this.activeRouteIndex || i < 0 || i >= this.routeList.length) return;
        this.activeRouteIndex = i;
        this.drawActiveRoute();
        await this.matchRouteStations();
      },
      //拉取我们在线站点全量并缓存（scene=route_plan：后端按角色隔离可见站点）
      async ensureOurStations(){
        let cid = (this.canFilterCompany && this.formData.company_id) ? Number(this.formData.company_id) : 0;
        //缓存只在同一个车队筛选下复用；空数组不命中缓存，否则首次没数据时就再也拉不到了
        if (cid === this.stationsCacheCompanyId && this.ourStationsCache && this.ourStationsCache.length) return this.ourStationsCache;
        let params = { station_type: 'oil_station', scene: 'route_plan' };
        //选了车队：只取该车队的可加油站点
        if (cid > 0) params.company_id = cid;
        let stations = await this.$commonFun.postProxy('admin/gas/getMapStations', params);
        let res = (stations && stations.data) ? stations.data : {};
        this.ourStationsCache = res.stations ? res.stations : [];
        this.restrictPoi = Number(res.restrict) === 1;
        this.stationsCacheCompanyId = cid;
        return this.ourStationsCache;
      },
      //切换车队：清掉站点缓存，已画出线路的话直接重新匹配一次
      async onCompanyChange(){
        this.ourStationsCache = [];
        if (this.routeList.length && this.routeList[this.activeRouteIndex]) await this.matchRouteStations();
      },
      //切换高德POI图层：取消勾选后只保留我们的站点，重新勾选时再检索沿线POI
      async onToggleAmap(){
        if (this.routeList.length && this.routeList[this.activeRouteIndex]) await this.matchRouteStations();
      },
      //沿线匹配：我们的在线站点 + 高德加油站POI，都取到折线垂距 ≤ 阈值的
      async matchRouteStations(){
        let route = this.routeList[this.activeRouteIndex];
        if (!route) return;
        let pts = route.points;
        let th = this.routeThreshold;
        //路线外接矩形（按阈值外扩），先粗筛再算垂距，减少计算量
        let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
        pts.forEach((p)=>{ if (p[0] < minLng) minLng = p[0]; if (p[0] > maxLng) maxLng = p[0]; if (p[1] < minLat) minLat = p[1]; if (p[1] > maxLat) maxLat = p[1]; });
        let midLat = (minLat + maxLat) / 2;
        let padLat = th / 111320;
        let padLng = th / (111320 * Math.cos(midLat * Math.PI / 180));

        let matched = [];
        //1) 我们的在线站点
        let ours = await this.ensureOurStations();
        for (const item of ours){
          let lng = parseFloat(item.longitude), lat = parseFloat(item.latitude);
          if (!isFinite(lng) || !isFinite(lat)) continue;
          if (lng < minLng - padLng || lng > maxLng + padLng || lat < minLat - padLat || lat > maxLat + padLat) continue;
          let d = this.minDistToPolyline([lng, lat], pts);
          if (d > th) continue;
          matched.push({ lnglat: [lng, lat], weight: 1, station: Object.assign({}, item, { distance: d / 1000, our_tag: 1 }) });
        }
        //2) 高德加油站POI：沿折线等距取样后就近搜索，合并去重再按垂距筛选（受限账号或未勾选时不检索）
        if (this.canShowAmap){
          let seen = {};
          let samples = this.sampleAlongRoute(pts, th);
          for (const s of samples){
            let pois = await this.searchNearbyGas(s, th);
            for (const p of pois){
              let lng = p.location.getLng(), lat = p.location.getLat();
              let kk = (p.id || p.name) + '_' + lng.toFixed(4) + ',' + lat.toFixed(4);
              if (seen[kk]) continue;
              seen[kk] = 1;
              let d = this.minDistToPolyline([lng, lat], pts);
              if (d > th) continue;
              matched.push({ lnglat: [lng, lat], weight: 1, station: {
                gas_name: p.name,
                gas_address: p.address || '',
                longitude: lng,
                latitude: lat,
                icon: 'https://oil.scsasy.com/static/images/icon/oil_station.png',
                distance: d / 1000,
                our_tag: 0,
              }});
            }
          }
        }
        matched.sort((a, b)=> a.station.distance - b.station.distance);
        this.matchedCount = matched.length;
        this.ourMatchedCount = matched.filter((m)=> m.station.our_tag).length;
        this.poiMatchedCount = this.matchedCount - this.ourMatchedCount;
        this.renderStationCluster(matched);
        if (!matched.length){
          //分三种情况提示：选了车队 / 受限账号名下没站点 / 有站点但不在这条线上
          if (this.canFilterCompany && this.formData.company_id){
            this.$Message.info('所选车队沿线 ' + (th / 1000).toFixed(2) + ' 公里内无可加油站点（该车队共 ' + ours.length + ' 个站点）');
          }else if (this.restrictPoi && !ours.length){
            this.$Message.info('当前账号名下暂无沿线站点');
          }else if (this.restrictPoi){
            this.$Message.info('沿线 ' + (th / 1000).toFixed(2) + ' 公里内没有您名下的站点（您名下共 ' + ours.length + ' 个站点）');
          }else if (!this.canShowAmap){
            this.$Message.info('沿线 ' + (th / 1000).toFixed(2) + ' 公里内没有我们的站点（高德加油站图层未勾选，勾选后可一并看周边第三方加油站）');
          }else{
            this.$Message.info('沿线 ' + (th / 1000).toFixed(2) + ' 公里内未匹配到站点');
          }
        }
      },
      //沿折线按阈值等距取样，取样次数上限 20（控制高德请求量）
      sampleAlongRoute(pts, th){
        let out = [];
        if (pts.length < 2) return out;
        let maxSamples = 20;
        let latAvg = pts.reduce((s, p)=> s + p[1], 0) / pts.length;
        let kx = 111320 * Math.cos(latAvg * Math.PI / 180), ky = 111320;
        let cum = [0];
        for (let i = 1; i < pts.length; i++){
          let dx = (pts[i][0] - pts[i - 1][0]) * kx, dy = (pts[i][1] - pts[i - 1][1]) * ky;
          cum.push(cum[i - 1] + Math.sqrt(dx * dx + dy * dy));
        }
        let total = cum[cum.length - 1];
        if (total <= 0){ out.push(pts[0]); return out; }
        let step = th;
        if (total / step > maxSamples) step = total / maxSamples;
        let next = 0, idx = 1;
        while (next <= total){
          while (idx < cum.length && cum[idx] < next) idx++;
          if (idx >= cum.length) break;
          let segLen = (cum[idx] - cum[idx - 1]) || 1;
          let t = (next - cum[idx - 1]) / segLen;
          out.push([pts[idx - 1][0] + (pts[idx][0] - pts[idx - 1][0]) * t, pts[idx - 1][1] + (pts[idx][1] - pts[idx - 1][1]) * t]);
          next += step;
        }
        if (!out.length) out.push(pts[0]);
        return out;
      },
      //点到折线的最小垂距（米）
      minDistToPolyline(p, pts){
        let min = Infinity;
        for (let i = 0; i < pts.length - 1; i++){
          let d = this.distPointToSegment(p, pts[i], pts[i + 1]);
          if (d < min) min = d;
        }
        return min;
      },
      //点到线段距离（米），p/a/b 均为 [lng,lat]，按局部平面近似（移植自小程序）
      distPointToSegment(p, a, b){
        let lat0 = (a[1] + b[1]) / 2;
        let kLat = 111320;
        let kLon = 111320 * Math.cos(lat0 * Math.PI / 180);
        let ax = a[0] * kLon, ay = a[1] * kLat;
        let bx = b[0] * kLon, by = b[1] * kLat;
        let px = p[0] * kLon, py = p[1] * kLat;
        let vx = bx - ax, vy = by - ay, wx = px - ax, wy = py - ay;
        let vv = vx * vx + vy * vy;
        if (vv === 0){ let dx = px - ax, dy = py - ay; return Math.sqrt(dx * dx + dy * dy); }
        let t = (wx * vx + wy * vy) / vv;
        t = Math.max(0, Math.min(1, t));
        let cx = ax + t * vx, cy = ay + t * vy;
        let dx = px - cx, dy = py - cy;
        return Math.sqrt(dx * dx + dy * dy);
      },
      //用高德 PlaceSearch 搜索中心点周边 radius 米内的加油站，返回 POI 数组
      searchNearbyGas(center, radius){
        return new Promise((resolve)=>{
          if (!this.placeSearch){
            this.placeSearch = new this.AMap.PlaceSearch({
              pageSize: 50,
              pageIndex: 1,
              extensions: 'all',
            });
          }
          this.placeSearch.searchNearBy('加油站', center, radius, (status, result)=>{
            if (status === 'complete' && result.poiList && result.poiList.pois){
              resolve(result.poiList.pois);
            }else{
              console.warn('高德周边搜索失败:', status, result);
              resolve([]);
            }
          });
        });
      },
      //把地址文本解析为经纬度坐标 [lng, lat]，失败返回 null
      geocodeAddress(address){
        return new Promise((resolve)=>{
          this.geocoder.getLocation(address, (status, result)=>{
            if (status === 'complete' && result.geocodes && result.geocodes.length){
              let loc = result.geocodes[0].location;
              resolve([loc.getLng(), loc.getLat()]);
            }else{
              console.warn('高德地址解析失败:', status, result);
              resolve(null);
            }
          });
        });
      },
      //用 PlaceSearch 按关键字定位（默认全国检索，对模糊地址更宽容），失败返回 null
      locateByPoi(keyword){
        return new Promise((resolve)=>{
          if (!this.placeSearch){
            this.placeSearch = new this.AMap.PlaceSearch({
              pageSize: 50,
              pageIndex: 1,
              extensions: 'all',
            });
          }
          this.placeSearch.search(keyword, (status, result)=>{
            if (status === 'complete' && result.poiList && result.poiList.pois && result.poiList.pois.length){
              let loc = result.poiList.pois[0].location;
              resolve([loc.getLng(), loc.getLat()]);
            }else{
              console.warn('高德地址POI定位失败:', status, result);
              resolve(null);
            }
          });
        });
      },
      //渲染/更新站点聚合：首次创建 MarkerCluster，之后用 setData 更新数据
      renderStationCluster(data){
        if (this.cluster){
          this.cluster.setData(data);
          return;
        }
        this.cluster = new this.AMap.MarkerCluster(this.map, data, {
          gridSize: 60,
          maxZoom: 12,
          //单个站点：原站点图标 + 右上角来源小圆点（绿=我们的站点，黄=高德POI）
          renderMarker: (ctx)=>{
            let item = ctx.data[0].station;
            let badge = item.our_tag ? '#19be6b' : '#ffb300';
            ctx.marker.setContent('<div style="position:relative;width:30px;height:30px;display:block">'
              + '<img src="' + item.icon + '" style="width:30px;height:30px;display:block"/>'
              + '<span style="position:absolute;top:-3px;right:-3px;width:10px;height:10px;border-radius:50%;background:' + badge + ';border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,.4)"></span>'
              + '</div>');
            ctx.marker.setOffset(new this.AMap.Pixel(-15, -15));
            ctx.marker.__station = item;
            if (!ctx.marker.__clickBound){
              ctx.marker.__clickBound = true;
              ctx.marker.on('click', ()=>{
                //点单个油站只弹详情，不缩放地图
                if (ctx.marker.__station) this.openInfoWindow(ctx.marker.__station);
              });
            }
          },
          //聚合点：高德 2.0 正确选项名为 renderClusterMarker（写成 renderCluster 会被忽略而用默认样式）
          renderClusterMarker: (ctx)=>{
            let count = ctx.count;
            let size = count > 20 ? 46 : 40;
            ctx.marker.setContent('<div style="width:' + size + 'px;height:' + size + 'px;line-height:' + size + 'px;font-size:' + (size > 40 ? 15 : 14) + 'px;background:#1e88e5;color:#fff;text-align:center;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3);cursor:pointer">' + count + '</div>');
            ctx.marker.setOffset(new this.AMap.Pixel(-size / 2, -size / 2));
            if (!ctx.marker.__clusterBound){
              ctx.marker.__clusterBound = true;
              ctx.marker.on('click', (e)=>{
                let lnglat = e && e.lnglat ? e.lnglat : ctx.marker.getPosition();
                if (lnglat) this.focusCluster([lnglat.getLng(), lnglat.getLat()]);
              });
            }
          },
        });
      },
      //点击聚合数字：以聚合点为中心直接放大三级并强制越过聚合级别，一次就把该区域内的站点展开
      focusCluster(pos){
        //maxZoom 为 12，取 13 保证该聚合必定散开成单个站点图标
        this.map.setZoomAndCenter(Math.min(this.map.getZoom() + 3, 13), pos);
      },
      //信息窗体：名称/地址/到线路垂距，我们的站点额外展示油机价与挂牌价
      openInfoWindow(item){
        let distHtml = '';
        if (item.distance !== undefined && item.distance !== null){
          distHtml = `<span style="color:#1e88e5">距线路：${Number(item.distance).toFixed(2)} 公里</span><br/>`;
        }
        let hasPrice = (v) => (v !== undefined && v !== null && v !== '');
        let priceHtml = '';
        if (hasPrice(item.price)){
          priceHtml += `<span style="color:#e53935">油机价：${item.price} 元/L</span><br/>`;
        }
        if (hasPrice(item.ndrc_price)){
          priceHtml += `<span style="color:#e53935">挂牌价：${item.ndrc_price} 元/L</span><br/>`;
        }
        let sourceHtml = item.our_tag ? '<span style="color:#19be6b">我们的站点</span>' : '<span style="color:#ff9900">高德加油站</span>';
        let content = [
          '<div>',
          `<b>${item.gas_name}</b><br/>`,
          `<span>${item.gas_address}</span><br/>`,
          sourceHtml,
          '<br/>',
          distHtml,
          priceHtml,
          '</div>'
        ];
        let infoWindow = new this.AMap.InfoWindow({
          anchor: 'top',
          offset: [15, -10],
          content: content.join("")
        });
        infoWindow.open(this.map, [item.longitude, item.latitude]);
      },
      //清空：路线、起终点、沿线站点与表单
      clearAll(){
        if (this.routePolyline){ this.map.remove(this.routePolyline); this.routePolyline = null; }
        if (this.endpointMarkers){ this.map.remove(this.endpointMarkers); this.endpointMarkers = null; }
        if (this.cluster){ this.cluster.setData([]); }
        this.routeList = [];
        this.activeRouteIndex = 0;
        this.matchedCount = 0;
        this.ourMatchedCount = 0;
        this.poiMatchedCount = 0;
        this.picking = '';
        this.pickedCoords = {};
        this.formData.start = '';
        this.formData.end = '';
        this.formData.waypoints = [];
        this.formData.company_id = '';
        this.ourStationsCache = [];
      },
    },
    created() {
      //非响应式数据：各选点键对应的精确坐标，规划时优先使用
      this.pickedCoords = {};
      //站点缓存对应的车队筛选条件（0 表示不限车队）
      this.stationsCacheCompanyId = 0;
    },
    mounted() {
      this.initMap();
      //车队筛选项的数据源（与车队卡页面同一个接口）
      if (this.canFilterCompany){
        this.$commonFun.postProxy('admin/company/getAllCompany', {}).then((res)=>{
          if (res && res.code === 200) this.companyList = res.data || [];
        });
      }
      this.$nextTick(() => {
        this.calcMapHeight();
      });
      window.addEventListener('resize', this.calcMapHeight);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.calcMapHeight);
    },
  }
</script>

<style scoped>
  .route-plan{
    padding: 0 8px;
  }
  .search-form{
    padding-top: 8px;
  }
  .point-row{
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .point-rows .point-row{
    margin-bottom: 6px;
  }
  .route-tabs{
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 0 8px;
  }
  .tabs-label{
    font-size: 13px;
    color: #515a6e;
  }
  .route-tip{
    margin-left: 12px;
    font-size: 12px;
    color: #808695;
  }
  .dot{
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin: 0 4px 0 10px;
    vertical-align: 0;
  }
  .pick-tip{
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    padding: 6px 10px;
    margin-bottom: 8px;
    font-size: 13px;
    color: #ad6800;
  }
  .map-box{
    width: 100%;
    border: 1px solid #e8eaec;
    border-radius: 4px;
  }
</style>
